import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useMemo } from 'react';

import { Button, Flex, Heading, Text } from '@/components/core';
import { LinkIcon } from '@/components/icons';
import { CradlLogo, ExportLogo } from '@/components/logos';
import { useGetNotConfiguredExports, useTour } from '@/hooks';
import { toAgentId, useListAgentRuns } from '@/hooks/api';
import { isValidationTaskId } from '@/utils';

export type SetupAutomaticExportBannerProps = {
  agentId: string;
};

const ISetupAutomaticExportBanner = ({ agentId }: SetupAutomaticExportBannerProps) => {
  const navigate = useNavigate();
  const goToSetup = useCallback(
    () => navigate({ to: '/agents/$agentId/integration', params: { agentId } }),
    [navigate, agentId]
  );

  const { actions } = useGetNotConfiguredExports({ agentId });
  const { data: agentRuns } = useListAgentRuns(toAgentId(agentId));

  const action = actions[0];
  const { showSetupAutomaticExportBanner, setShowSetupAutomaticExportBanner } = useTour({
    prefixes: [agentId, action?.actionId],
  });

  const dismiss = useCallback(() => setShowSetupAutomaticExportBanner(false), [setShowSetupAutomaticExportBanner]);

  const hasValidatedRuns = useMemo(() => {
    return agentRuns?.data?.some((run) => {
      return run.events
        ?.filter((event) => isValidationTaskId(event.resourceId))
        ?.some((event) => event.status === 'succeeded');
    });
  }, [agentRuns?.data]);

  if (!action || !showSetupAutomaticExportBanner || !hasValidatedRuns) return;

  return (
    <Flex
      variant="primary"
      className="border-primary-100 bg-primary-25 gap-sm p-md w-full items-start rounded-lg border-1 shadow-xs"
    >
      <div className="flex items-center">
        <CradlLogo className="translate-x-sm shadow-lg" size="2xl" />
        <Flex variant="plain" className="p-xs z-10 rounded-full">
          <LinkIcon className="size-md" />
        </Flex>
        <ExportLogo className="-translate-x-sm shadow-lg" size="2xl" functionId={action.functionId} />
      </div>
      <Flex col variant="plain" className="gap-sm">
        <Flex col className="gap-xs">
          <Heading slot="title">Setup automatic export</Heading>
          <Text>
            To automatically export your parsed document data, you need to complete the setup of your integration in the
            workflow.
          </Text>
        </Flex>

        <div className="gap-sm flex">
          <Button variant="secondary" onPress={goToSetup}>
            <Text>Go to setup</Text>
          </Button>
          <Button variant="plain" onPress={dismiss}>
            <Text>Dismiss</Text>
          </Button>
        </div>
      </Flex>
    </Flex>
  );
};

export const SetupAutomaticExportBanner = memo(ISetupAutomaticExportBanner) as typeof ISetupAutomaticExportBanner;
